import React, { useState, useMemo } from 'react';
import { Table, Card, DatePicker, Button, message, Tabs, Row, Col, Statistic, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { CalculatorOutlined, DownloadOutlined, DatabaseOutlined, ArrowUpOutlined, ArrowDownOutlined, BarChartOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { theme } from '../../../theme';

const { Option } = Select;

interface InventarioItem {
  id: number;
  insumo: string;
  ubicacion: string;
  ingresos: number;
  egresos: number;
  stock: number;
}

const Inventario: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<InventarioItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fecha, setFecha] = useState<Dayjs>(dayjs());
  const [filtroInsumo, setFiltroInsumo] = useState<string>('todos');
  const [filtroUbicacion, setFiltroUbicacion] = useState<string>('todos');

  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  const columns: ColumnsType<InventarioItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      align: 'right',
      width: 80,
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo',
      key: 'insumo',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.insumo))).map(insumo => ({
        text: insumo,
        value: insumo,
      })),
      onFilter: (value, record) => record.insumo === value,
    },
    {
      title: 'Ubicación',
      dataIndex: 'ubicacion',
      key: 'ubicacion',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.ubicacion))).map(ubicacion => ({
        text: ubicacion,
        value: ubicacion,
      })),
      onFilter: (value, record) => record.ubicacion === value,
    },
    {
      title: 'Ingresos',
      dataIndex: 'ingresos',
      key: 'ingresos',
      align: 'right',
    },
    {
      title: 'Egresos',
      dataIndex: 'egresos',
      key: 'egresos',
      align: 'right',
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      align: 'right',
    },
  ];

  const calcularInventario = async () => {
    if (!empresa || !sucursal) {
      message.error('Faltan datos necesarios para la consulta');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const fechaStr = fecha.format('YYYY-MM-DD');
      
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/inventario/${empresa}/${sucursal}/${fechaStr}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener datos');
      }

      const responseData = await response.json() as InventarioItem[];
      setData(responseData);
      message.success('Inventario calculado correctamente');
      
    } catch (error) {
      message.error('Error al calcular el inventario');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cálculo de estadísticas
  const estadisticas = useMemo(() => {
    // Filtrar datos según filtros seleccionados
    let datosFiltrados = data;
    if (filtroInsumo !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.insumo === filtroInsumo);
    }
    if (filtroUbicacion !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.ubicacion === filtroUbicacion);
    }

    const totalRegistros = datosFiltrados.length;
    const totalIngresos = datosFiltrados.reduce((sum, item) => sum + (item.ingresos || 0), 0);
    const totalEgresos = datosFiltrados.reduce((sum, item) => sum + (item.egresos || 0), 0);
    const totalStock = datosFiltrados.reduce((sum, item) => sum + (item.stock || 0), 0);
    const promedioStock = totalRegistros > 0 ? totalStock / totalRegistros : 0;
    
    // Distribución por insumo
    const porInsumo = datosFiltrados.reduce((acc, item) => {
      const insumo = item.insumo || 'Sin insumo';
      if (!acc[insumo]) {
        acc[insumo] = { ingresos: 0, egresos: 0, stock: 0 };
      }
      acc[insumo].ingresos += item.ingresos || 0;
      acc[insumo].egresos += item.egresos || 0;
      acc[insumo].stock += item.stock || 0;
      return acc;
    }, {} as Record<string, { ingresos: number; egresos: number; stock: number }>);
    
    const distribucionInsumo = Object.entries(porInsumo)
      .map(([insumo, datos]) => ({
        insumo,
        ingresos: datos.ingresos,
        egresos: datos.egresos,
        stock: datos.stock
      }))
      .sort((a, b) => b.stock - a.stock)
      .slice(0, 10);
    
    // Distribución por ubicación
    const porUbicacion = datosFiltrados.reduce((acc, item) => {
      const ubicacion = item.ubicacion || 'Sin ubicación';
      if (!acc[ubicacion]) {
        acc[ubicacion] = { ingresos: 0, egresos: 0, stock: 0 };
      }
      acc[ubicacion].ingresos += item.ingresos || 0;
      acc[ubicacion].egresos += item.egresos || 0;
      acc[ubicacion].stock += item.stock || 0;
      return acc;
    }, {} as Record<string, { ingresos: number; egresos: number; stock: number }>);
    
    const distribucionUbicacion = Object.entries(porUbicacion)
      .map(([ubicacion, datos]) => ({
        ubicacion,
        ingresos: datos.ingresos,
        egresos: datos.egresos,
        stock: datos.stock
      }))
      .sort((a, b) => b.stock - a.stock)
      .slice(0, 10);
    
    // Insumos con stock bajo (menos del 10% del promedio)
    const stockBajo = datosFiltrados
      .filter(item => item.stock > 0 && item.stock < promedioStock * 0.1)
      .sort((a, b) => a.stock - b.stock)
      .slice(0, 10);
    
    const insumosUnicos = Array.from(new Set(data.map(item => item.insumo))).sort();
    const ubicacionesUnicas = Array.from(new Set(data.map(item => item.ubicacion))).sort();
    
    return {
      totalRegistros,
      totalIngresos,
      totalEgresos,
      totalStock,
      promedioStock,
      distribucionInsumo,
      distribucionUbicacion,
      stockBajo,
      insumosUnicos,
      ubicacionesUnicas
    };
  }, [data, filtroInsumo, filtroUbicacion]);

  const exportToExcel = () => {
    // Preparar los datos para Excel
    const excelData = data.map(item => ({
      'ID': item.id,
      'Insumo': item.insumo,
      'Ubicación': item.ubicacion,
      'Ingresos': Number(item.ingresos),
      'Egresos': Number(item.egresos),
      'Stock': Number(item.stock)
    }));

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Configurar formato de números para las columnas numéricas
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let C = range.s.c; C <= range.e.c; C++) {
      for (let R = range.s.r; R <= range.e.r; R++) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && typeof cell.v === 'number') {
          cell.z = '#,##0.00'; // Formato de número con 2 decimales
        }
      }
    }

    // Agregar worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Inventario');

    // Guardar archivo
    XLSX.writeFile(wb, `Inventario_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Inventario
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Inventario de Insumos"
      >
        <div style={{ padding: '24px' }}>
          {/* Filtros */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '20px',
            marginBottom: '24px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Fecha
              </label>
              <DatePicker
                value={fecha}
                onChange={(date) => date && setFecha(date)}
                format="DD/MM/YYYY"
                size="large"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Button 
                type="primary" 
                icon={<CalculatorOutlined />}
                onClick={calcularInventario}
                loading={loading}
                size="small"
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontWeight: 500,
                  width: '100%'
                }}
              >
                Calcular
              </Button>
            </div>
            <div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={data.length === 0}
                size="small"
                style={{
                  fontWeight: 500,
                  width: '100%',
                  opacity: data.length === 0 ? 0.6 : 1
                }}
              >
                Exportar Excel
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            defaultActiveKey="listado"
            size="small"
            items={[
              {
                key: 'listado',
                label: 'Listado',
                children: (
                  <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    scroll={{ x: 1000 }}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`,
                    }}
                  />
                )
              },
              {
                key: 'estadisticas',
                label: 'Estadísticas',
                children: (
                  <div style={{ padding: '20px 0' }}>
                    {/* Filtros */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Insumo:</div>
                        <Select
                          value={filtroInsumo}
                          onChange={setFiltroInsumo}
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Todos los insumos"
                        >
                          <Option value="todos">Todos</Option>
                          {estadisticas.insumosUnicos.map(ins => (
                            <Option key={ins} value={ins}>{ins}</Option>
                          ))}
                        </Select>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Ubicación:</div>
                        <Select
                          value={filtroUbicacion}
                          onChange={setFiltroUbicacion}
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Todas las ubicaciones"
                        >
                          <Option value="todos">Todas</Option>
                          {estadisticas.ubicacionesUnicas.map(ub => (
                            <Option key={ub} value={ub}>{ub}</Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>

                    {/* Tarjetas de resumen */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total de Registros"
                            value={estadisticas.totalRegistros}
                            prefix={<DatabaseOutlined />}
                            valueStyle={{ color: theme.colors.primary }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Ingresos"
                            value={estadisticas.totalIngresos}
                            prefix={<ArrowUpOutlined />}
                            valueStyle={{ color: theme.colors.success }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Egresos"
                            value={estadisticas.totalEgresos}
                            prefix={<ArrowDownOutlined />}
                            valueStyle={{ color: theme.colors.error }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Stock"
                            value={estadisticas.totalStock}
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: theme.colors.info }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Promedio Stock"
                            value={estadisticas.promedioStock.toFixed(2)}
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: theme.colors.warning }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Distribución por Insumo */}
                    <Card 
                      title="Top 10 - Distribución por Insumo" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionInsumo}
                        rowKey="insumo"
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Insumo',
                            dataIndex: 'insumo',
                            key: 'insumo',
                          },
                          {
                            title: 'Ingresos',
                            dataIndex: 'ingresos',
                            key: 'ingresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Egresos',
                            dataIndex: 'egresos',
                            key: 'egresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Stock',
                            dataIndex: 'stock',
                            key: 'stock',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                        ]}
                      />
                    </Card>

                    {/* Distribución por Ubicación */}
                    <Card 
                      title="Top 10 - Distribución por Ubicación" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionUbicacion}
                        rowKey="ubicacion"
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Ubicación',
                            dataIndex: 'ubicacion',
                            key: 'ubicacion',
                          },
                          {
                            title: 'Ingresos',
                            dataIndex: 'ingresos',
                            key: 'ingresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Egresos',
                            dataIndex: 'egresos',
                            key: 'egresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Stock',
                            dataIndex: 'stock',
                            key: 'stock',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                        ]}
                      />
                    </Card>

                    {/* Stock Bajo */}
                    {estadisticas.stockBajo.length > 0 && (
                      <Card 
                        title="Insumos con Stock Bajo (Alerta)" 
                        style={{ marginBottom: '24px' }}
                      >
                        <Table
                          dataSource={estadisticas.stockBajo}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Insumo',
                              dataIndex: 'insumo',
                              key: 'insumo',
                            },
                            {
                              title: 'Ubicación',
                              dataIndex: 'ubicacion',
                              key: 'ubicacion',
                            },
                            {
                              title: 'Stock',
                              dataIndex: 'stock',
                              key: 'stock',
                              align: 'right' as const,
                              render: (valor: number) => (
                                <span style={{ color: theme.colors.error, fontWeight: 'bold' }}>
                                  {valor.toLocaleString('es-AR')}
                                </span>
                              ),
                            },
                          ]}
                        />
                      </Card>
                    )}
                  </div>
                )
              }
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

export default Inventario;
